import { useState, useEffect } from 'react';
import axios from 'axios';
import { MousePointerClick, Users, Briefcase, DollarSign, Copy, Check, ExternalLink } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const MyPerformance = ({ token }) => {
  const [data, setData] = useState(null);
  const [links, setLinks] = useState([]);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const [perfRes, linksRes] = await Promise.all([
          axios.get(`${API}/salesperson/performance`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/tracking-links/analytics/salesperson`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setData(perfRes.data);
        setLinks(linksRes.data);
      } catch { /* silent */ }
    };
    load();
  }, [token]);

  if (!data) return null;

  const copyLink = (url, id) => {
    try {
      navigator.clipboard.writeText(url);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Fallback for restricted contexts
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const stats = [
    { label: 'Clicks', value: data.total_clicks, icon: MousePointerClick, color: 'text-blue-400 bg-blue-400/10' },
    { label: 'Demos', value: data.total_demos, icon: Users, color: 'text-purple-400 bg-purple-400/10' },
    { label: 'Deals Closed', value: data.total_closed_won, icon: Briefcase, color: 'text-green-400 bg-green-400/10' },
    { label: 'Revenue', value: `$${data.total_revenue.toLocaleString()}`, icon: DollarSign, color: 'text-brand-gold bg-brand-gold/10' },
  ];

  return (
    <div className="rounded-xl border border-white/10 bg-[#111317] p-5 mb-6" data-testid="my-performance-section">
      <h3 className="text-sm font-semibold text-brand-text mb-4">My Performance</h3>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="rounded-lg border border-zinc-800 bg-brand-bg px-3 py-2.5 text-center" data-testid={`perf-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className={`w-7 h-7 rounded-md ${s.color} flex items-center justify-center mx-auto mb-1.5`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="text-lg font-bold text-brand-text leading-none">{s.value}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Tracking Links */}
      {links.length > 0 && (
        <div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Your Tracking Links</div>
          <div className="space-y-2">
            {links.map((link) => (
              <div key={link.id} className="flex items-center justify-between gap-3 rounded-lg bg-brand-bg border border-zinc-800 px-3 py-2" data-testid="tracking-link-row">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-brand-text font-medium truncate">{link.post_title}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{link.short_url}</div>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-zinc-500 flex-shrink-0">
                  <span>{link.clicks} clicks</span>
                  <button
                    onClick={() => copyLink(link.short_url, link.id)}
                    className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-brand-gold transition-colors"
                    data-testid={`copy-link-${link.id}`}
                  >
                    {copied === link.id ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPerformance;
